import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import {
  classifyAreaMatch,
  portLabel,
  type AreaMatcher,
} from "@/lib/portAreaMatchUtils";

const upper = (s: string): string => (s || "").trim().toUpperCase();

interface PortAreaBannerProps {
  matcher: AreaMatcher;
}

/**
 * Info banner explaining that the searched origin was expanded to its mapped
 * port area, listing the additional sibling ports now included in the results.
 */
export function PortAreaBanner({ matcher }: PortAreaBannerProps) {
  if (!matcher.hasExpansion) return null;

  const lines = matcher.expansions
    .map((exp) => {
      const req = upper(exp.requested);
      const siblings = exp.members.filter((m) => upper(m.locode) !== req);
      if (siblings.length === 0) return null;
      const reqLabel = portLabel(req, exp.requested_name);
      const siblingLabels = siblings
        .map((m) => portLabel(upper(m.locode), m.name))
        .join(", ");
      return (
        <div key={req}>
          <strong>{reqLabel}</strong> also shows area ports: {siblingLabels}
        </div>
      );
    })
    .filter(Boolean);

  if (lines.length === 0) return null;

  return (
    <div className="min-w-0 rounded-lg border border-sky-500/30 bg-sky-500/8 px-3 py-2 text-xs">
      <p className="font-medium text-foreground">Showing mapped port areas</p>
      <div className="mt-1 space-y-0.5 break-words text-muted-foreground">
        {lines}
      </div>
    </div>
  );
}

interface PortAreaMatchBadgeProps {
  /** The result row's origin LOCODE. */
  locode: string | null | undefined;
  matcher: AreaMatcher;
  className?: string;
}

/**
 * Per-row badge marking whether a result matched the searched origin exactly
 * or via its mapped area. Renders nothing for exact matches to keep tables clean.
 */
export function PortAreaMatchBadge({
  locode,
  matcher,
  className,
}: PortAreaMatchBadgeProps) {
  const match = classifyAreaMatch(matcher, locode);
  if (match.kind !== "area" || !match.requested) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex shrink-0 cursor-pointer border-0 bg-transparent p-0"
        >
          <Badge variant="info" className={className}>
            Area: {match.requested}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-xs p-3 text-xs" side="top">
        Shown because <strong>{upper(locode ?? "")}</strong> is in the{" "}
        <strong>{match.requested}</strong> area.
      </PopoverContent>
    </Popover>
  );
}
